import App from 'modules/app';
import { Redirect, Route, RouteComponentProps, Router, Switch } from 'react-router-dom';
import React, { PureComponent } from 'react';
import { convertPathToHashBased, generatePathnameWithLangCode, setCorrectLocaleParam } from 'utils/RouteHelper';
import basePath from 'utils/basePath';
import { realAccount$ } from '../singletons';
import { getLangFromUrl, Translator } from 'Translator';
import ErrorBoundary from 'components/ErrorBoundary';
import { TrekkerProvider } from 'utils/analytics';
import { Toaster } from 'react-hot-toast';
import { isCordova } from 'utils/platform';

class Root extends PureComponent<RouteComponentProps<{ locale: string }>> {
  setLocale: boolean;

  componentDidMount() {
    convertPathToHashBased(this.props);

    realAccount$
      .filter(account => !!account.account)
      .map((account) => {
        // if we have locale, use that
        if (this.props.match.params.locale) {
          return this.props.match.params.locale;
        }

        // if we have locale in url (no matter how), use that
        if (getLangFromUrl()) {
          return getLangFromUrl();
        }

        // if we don't have a locale in url, use lang from account
        return account.account.preferredLang;
      })
      .filter(preferredLang => Translator.getLanguageCode() !== preferredLang && !!preferredLang)
      .subscribe((preferredLang) => {
        Translator.setLanguageCode(preferredLang);
      });

    // do not handle the language$ stream in cordova.
    // This stream will cancel running requests (e.g. login requests)
    // when it receives a new value because of the window.location.reload(), which
    // triggers the "forceTokenRemoval" logic which logs users out after having logged in
    if (!isCordova()) {
      Translator.language$
        .filter(preferredLang => (!this.props.match.params.locale && preferredLang === 'en') || (this.props.match.params.locale !== preferredLang && !!preferredLang))
        .map(preferredLang => generatePathnameWithLangCode(this.props.location.pathname, preferredLang))
        .filter(newPathname => newPathname !== this.props.location.pathname)
        .subscribe((newPathname) => {
          if (!this.setLocale) {
            // change url to new pathname first
            this.props.history.replace({
              ...this.props.location,
              pathname: newPathname
            });

            // then hard-refresh it
            window.location.reload();
          }
        });
    }

  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.match.params.locale !== this.props.match.params.locale) {
      this.setLocale = setCorrectLocaleParam(nextProps);
    }
  }

  render() {
    return (
      <ErrorBoundary>
        <TrekkerProvider context={{
          path: this.props.location.pathname,
          queryString: this.props.location.search
        }}>
          <App />
        </TrekkerProvider>
      </ErrorBoundary>
    );
  }
}

const addTrailingSlash = (props: RouteComponentProps<{ locale: string; 'url*': string }, {}, { modal?: any }>) => {
  if (props.location?.state?.modal) {
    return <Root {...props} />;
  }
  const [pathnameWithoutQuery, ...query] = props.location.pathname.split('?');
  if (pathnameWithoutQuery.endsWith('/')) {
    return <Root {...props} />;
  }
  const queryCombined = query.join('?');

  if (ENVIRONMENT !== 'production' && !pathnameWithoutQuery.endsWith('/') && !pathnameWithoutQuery.endsWith('/index.html')) {
    console.error(`${pathnameWithoutQuery} path should have a trailing slash`);
  }

  const pathnameWithSlash = pathnameWithoutQuery.endsWith('/') ? pathnameWithoutQuery : `${pathnameWithoutQuery}/`;
  const pathnameFull = `${pathnameWithSlash}${queryCombined ? '?' + queryCombined : ''}`;
  return (
    <Redirect
      to={{
        ...props.location,
        pathname: pathnameFull
      }}
    />
  );
};

export default ({ history }) => {
  return (
    <Router history={history}>
      <Switch>
        <Route path="/index.html" exact strict render={() => <Redirect to="/" />} />
        <Route path="/:url*" exact strict render={addTrailingSlash} />
        <Route path={basePath} component={Root} />
      </Switch>
      <Toaster />
    </Router>
  );
};
